import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api';
import MaestraLogo from '../components/MaestraLogo';
import PlatformFooter from '../components/PlatformFooter';

const PERIODS = [
  { id: '1_month', label: '1 month', desc: '30 days of full access' },
  { id: '3_months', label: '3 months', desc: '90 days of full access' },
  { id: '6_months', label: '6 months', desc: '180 days of full access' },
  { id: '12_months', label: '12 months', desc: '365 days of full access' },
];

const ExtendAccess = () => {
  const navigate = useNavigate();
  document.title = 'Maestra - Extend Access';

  const [selected, setSelected] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [hasPending, setHasPending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAPI.getRenewalStatus().then(res => {
      setHasPending(res.data.has_pending);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setError('');
    setSubmitting(true);
    try {
      await authAPI.createRenewalRequest(selected, message);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="login-container" style={{ maxWidth: '460px' }}>
          <div style={{ marginBottom: '32px' }}><MaestraLogo size="sm" /></div>

          {submitted ? (
            <div data-testid="extend-success" style={{ textAlign: 'center' }}>
              <h1 className="login-title">Request submitted</h1>
              <p style={{
                fontSize: '13px', color: 'var(--kahla-text-light)', lineHeight: '1.7',
                letterSpacing: '0.02em', marginBottom: '24px',
              }}>
                We've received your extension request.<br />
                You'll hear from us soon.
              </p>
            </div>
          ) : hasPending ? (
            <div data-testid="extend-pending" style={{ textAlign: 'center' }}>
              <h1 className="login-title">Request pending</h1>
              <p style={{
                fontSize: '13px', color: 'var(--kahla-text-light)', lineHeight: '1.7',
                letterSpacing: '0.02em', marginBottom: '24px',
              }}>
                You already have a pending extension request.<br />
                We'll be in touch soon.
              </p>
            </div>
          ) : (
            <>
              <h1 className="login-title" data-testid="extend-title">Extend your access</h1>
              <p style={{
                fontSize: '13px', color: 'var(--kahla-text-light)', textAlign: 'center',
                marginBottom: '28px', lineHeight: '1.6', letterSpacing: '0.02em',
              }}>
                Choose how long you'd like to extend your workspace access.
              </p>

              {error && <div className="login-error" data-testid="extend-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                  {PERIODS.map(p => (
                    <label
                      key={p.id}
                      data-testid={`period-${p.id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '14px 16px',
                        border: `1px solid ${selected === p.id ? 'var(--kahla-text)' : 'var(--kahla-border)'}`,
                        background: selected === p.id ? '#faf8f5' : 'var(--kahla-white)',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s',
                      }}
                    >
                      <input
                        type="radio" name="period" value={p.id}
                        checked={selected === p.id}
                        onChange={() => setSelected(p.id)}
                        style={{ accentColor: 'var(--kahla-text)' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', color: 'var(--kahla-text)', fontWeight: '500' }}>{p.label}</div>
                        <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '2px' }}>{p.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '6px', letterSpacing: '0.03em' }}>
                    Message (optional)
                  </label>
                  <textarea
                    data-testid="extend-message"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Any notes for the admin..."
                    maxLength={500}
                    style={{
                      width: '100%', padding: '10px 12px', fontSize: '13px',
                      border: '1px solid var(--kahla-border)', background: 'var(--kahla-white)',
                      fontFamily: 'inherit', boxSizing: 'border-box', minHeight: '60px', resize: 'vertical',
                    }}
                  />
                </div>

                <button
                  type="submit" className="btn-primary" disabled={!selected || submitting}
                  data-testid="extend-submit-btn"
                  style={{ width: '100%', opacity: !selected ? 0.5 : 1 }}
                >
                  {submitting ? 'Submitting...' : 'Request extension'}
                </button>
              </form>
            </>
          )}

          <div style={{ marginTop: '28px', textAlign: 'center' }}>
            <button className="btn-secondary" onClick={() => navigate('/settings')} style={{ fontSize: '12px', padding: '10px 20px' }}>
              Back to Settings
            </button>
          </div>
        </div>
      </div>

      <footer style={{ width: '100vw', padding: '24px', textAlign: 'center', borderTop: '1px solid var(--kahla-border)', marginTop: 'auto' }}>
        <PlatformFooter variant="compact" />
      </footer>
    </div>
  );
};

export default ExtendAccess;
